/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from "react";
import {MRT_ColumnDef} from "material-react-table";
import {Box, Tab, Tabs, TextField} from "@mui/material";
import {cloneDeep} from "lodash";
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";

/**
 * @description Validates the input value based on the property name.
 * If the property is in the list of properties to ignore, it returns null.
 * @param newVal string | number | null
 * @param propertyName 
 * @returns  string | null
 * @returns null if the property in ignore list, validation for property value passes tests or an error message if validation fails.
 */
export const validateInput = (newVal: string | number | null, propertyName: string) => {
  const propertiesToIgnore = ["Wheelbase", "Comments"];

  // Check if the property should be ignored
  if (propertiesToIgnore.includes(propertyName)) {
    return null; // Ignore validation for these properties
  }

  const numVal = Number(newVal);
  if (isNaN(numVal)) {
    return "Enter a valid number.";
  }
  // Check for specific validation rules based on the property name
  const allowGreaterThanZero = ["Retroreflectivity"].includes(propertyName);
  const allowNegative = ["Radar Cross Section"].includes(propertyName);

  if (allowGreaterThanZero && numVal <= 0) {
    return "Number must be > 0.";
  }
  if (!allowNegative && numVal < 0) {
    return "Number must be >= 0.";
  }
  return null;
};

const ParameterProperties = (props: any) => {
  const [generalData, setGeneralData] = useState<any[]>([]);
  const [frontProfileData, setFrontProfileData] = useState<any[]>([]);
  const [sideProfileData, setSideProfileData] = useState<any[]>([]);
  const [rearProfileData, setRearProfileData] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState("General Properties");
  const [editableCell, setEditableCell] = useState<{ rowIndex: number; columnId: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string | null }>({});
  const [temporaryValue, setTemporaryValue] = useState<string | number | null>(null);

  useEffect(() => {
    if (props.object && props.object.parameters) {
      const {general, frontProfile, sideProfile, rearProfile} = props.object.parameters;

      const formatKey = (key: string) =>
        key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (str: string) => str.toUpperCase());

      // General data handling
      if (general) {
        const { comments, wheelbase } = general;

        const numericFields = [
          "minimumWidth",
          "maximumWidth",
          "minimumHeight",
          "maximumHeight",
          "minimumLength",
          "maximumLength",
        ];

        const data = numericFields.map((field) => ({
          property: formatKey(field),
          value: general[field] !== "" && general[field] != null ? general[field] : null,
          units: "meters",
          dbKey: field,
        }));

        data.push(
          { property: "Wheelbase", value: wheelbase ?? "", units: "meters", dbKey: "wheelbase" },
          { property: "Comments", value: comments ?? "", units: "", dbKey: "comments" }
        );

        setGeneralData(data);
      }

      // Similar logic for frontProfile, sideProfile, and rearProfile...
      const desiredKeys = ["orientationWidth", "orientationHeight", "radarCrossSection", "retroreflectivity"];

      const mapProfileData = (profile: any) =>
        Object.entries(profile)
          .filter(([key]) => desiredKeys.includes(key))
          .map(([key, value]) => ({
            property: formatKey(key),
            value: value !== "" && value != null ? value : null,
            units: key === "radarCrossSection" ? "dBsm" : key === "retroreflectivity" ? "cd/lx/m2" : "meters",
            dbKey: key,
          }));

      if (frontProfile) {
        const _data = mapProfileData(frontProfile);
        _data.push({ property: "Comments", value: frontProfile.comments ?? "", units: "", dbKey: "comments" });
        _data.push({ property: "ID", value: frontProfile.id ?? "", units: "", dbKey: "id" });
        setFrontProfileData(_data);
      }

      if (sideProfile) {
        const _data = mapProfileData(sideProfile);
        _data.push({ property: "Comments", value: sideProfile.comments ?? "", units: "", dbKey: "comments" });
        _data.push({ property: "ID", value: sideProfile.id ?? "", units: "", dbKey: "id" });
        setSideProfileData(_data);
      }

      if (rearProfile) {
        const _data = mapProfileData(rearProfile);
        _data.push({ property: "Comments", value: rearProfile.comments ?? "", units: "", dbKey: "comments" });
        _data.push({ property: "ID", value: rearProfile.id ?? "", units: "", dbKey: "id" });
        setRearProfileData(_data);
      }
    } else {
      setGeneralData([]);
      setFrontProfileData([]);
      setSideProfileData([]);
      setRearProfileData([]);
    }
  }, [props]);


  useEffect(() => {
    // console.log("General Data:", generalData);
    // console.log("Front Profile Data:", frontProfileData);
    // console.log("Side Profile Data:", sideProfileData);
    // console.log("Rear Profile Data:", rearProfileData);

    props.onDataUpdate({
      generalData,
      frontProfileData,
      sideProfileData,
      rearProfileData,
    })

    // props.object has the full object. we will send modified object here. need to use dbKey to update the object profiles
    const cpObj = cloneDeep(props.object)
    const { general, frontProfile, sideProfile, rearProfile } = cpObj.parameters;
    if (general) {
      const generalDataMap = Object.fromEntries(generalData.map((item) => [item.dbKey, item.value]));
      Object.assign(general, generalDataMap);
    }
    if (frontProfile) {
      const frontDataMap = Object.fromEntries(frontProfileData.map((item) => [item.dbKey, item.value]));
      Object.assign(frontProfile, frontDataMap);
    }
    if (sideProfile) {
      const sideDataMap = Object.fromEntries(sideProfileData.map((item) => [item.dbKey, item.value]));
      Object.assign(sideProfile, sideDataMap);
    }
    if (rearProfile) {
      const rearDataMap = Object.fromEntries(rearProfileData.map((item) => [item.dbKey, item.value]));
      Object.assign(rearProfile, rearDataMap);
    }

    props.onDataUpdate(cpObj);
  }, [generalData, frontProfileData, sideProfileData, rearProfileData, props.object]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    setEditableCell(null); // Clear editable cell on tab change
  };

  const handleCellClick = (rowIndex: number, columnId: string) => {
    let rowProperty;
    // Get the property name based on the selected tab
    switch (selectedTab) {
      case "General Properties":
        rowProperty = generalData[rowIndex]?.property;
        break;
      case "Front Profile":
        rowProperty = frontProfileData[rowIndex]?.property;
        break;
      case "Side Profile":
        rowProperty = sideProfileData[rowIndex]?.property;
        break;
      case "Rear Profile":
        rowProperty = rearProfileData[rowIndex]?.property;
        break;
      default:
        rowProperty = null;
    }

    // Prevent editing for "Orientation Width" and "Orientation Height"
    if (rowProperty === "Orientation Width" || rowProperty === "Orientation Height") {
      return; // Do nothing, prevent editing
    }

    if (columnId !== "property") {
      setEditableCell({ rowIndex, columnId });

      switch (selectedTab) {
        case "General Properties":
          setTemporaryValue(generalData[rowIndex][columnId]);
          break;
        case "Front Profile":
          setTemporaryValue(frontProfileData[rowIndex][columnId]);
          break;
        case "Side Profile":
          setTemporaryValue(sideProfileData[rowIndex][columnId]);
          break;
        case "Rear Profile":
          setTemporaryValue(rearProfileData[rowIndex][columnId]);
          break;
        default:
          break;
      }
    }
  };


  const compareInput = (newVal: string | number | null, propertyName: string) => {
    const numVal = Number(newVal);

    if (selectedTab === "General Properties") { // Use string tab name
      let relatedMaxValue, relatedMinValue;

      if (propertyName.includes("Minimum")) {
        const maxPropertyName = propertyName.replace("Minimum", "Maximum");
        relatedMaxValue = generalData.find((row) => row.property === maxPropertyName)?.value;

        if (relatedMaxValue && Number(relatedMaxValue) <= numVal) {
          return `Min must be <= Max.`;
        }
      } else if (propertyName.includes("Maximum")) {
        const minPropertyName = propertyName.replace("Maximum", "Minimum");
        relatedMinValue = generalData.find((row) => row.property === minPropertyName)?.value;

        if (relatedMinValue && Number(relatedMinValue) >= numVal) {
          return `Max must be >= Min.`;
        }
      }
    }
    return null; // No validation error
  };

  const numericFields = [
    "Minimum Width",
    "Maximum Width",
    "Minimum Height",
    "Maximum Height",
    "Minimum Length",
    "Maximum Length",
    "Orientation Width",
    "Orientation Height",
    "Radar Cross Section",
    "Retroreflectivity",
  ];

  /**
  * @description This function handles the change event for the editable cell.
  * It updates the temporary value and performs validation.
  * If the input is numeric and empty, it sets the value to null.
  *   
  * @param e - The change event from the input field.
  * @param rowProperty - The property name of the row being edited.
  * @returns void
  */
  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, rowProperty: string) => {
    const newValue = e.target.value;

    const isNumericField = numericFields.includes(rowProperty);

    // Check for basic validation first
    const errorMessage = validateInput(newValue, rowProperty);
    
    let processedValue: string | number | null = newValue
    if (isNumericField && newValue === "") {
      processedValue = null
    }

    // Then check for comparison logic on minimum and maximum values
    const comparisonMessage = compareInput(newValue, rowProperty);

    // If either validation or comparison fails, show error message
    if (errorMessage || comparisonMessage) {
      setTemporaryValue(processedValue);
      setValidationErrors((prev) => ({
        ...prev,
        [`row-${editableCell?.rowIndex}`]: errorMessage || comparisonMessage,
      }));
    } else {
      // If all checks pass, update the temporary value
      setTemporaryValue(processedValue);
      setValidationErrors((prev) => ({
        ...prev,
        [`row-${editableCell?.rowIndex}`]: null, // Clear error
      }));
    }
  };

/**
 * @description This function is called when the user finishes editing a cell.
 * Handles the blur event for the editable cell.
 * Validates the input value and updates the corresponding data based on the selected tab.
 * If validation fails, it reverts to the previous valid value.
 * @returns void
 */
  const handleCellBlur = () => {
    if (editableCell) {
      const rowIndex = editableCell.rowIndex;
      const columnId = editableCell.columnId;

      // Get the property name based on the selected tab
      const propertyName = (() => {
        switch (selectedTab) {
          case "General Properties":
            return generalData[rowIndex]?.property;
          case "Front Profile":
            return frontProfileData[rowIndex]?.property;
          case "Side Profile":
            return sideProfileData[rowIndex]?.property;
          case "Rear Profile":
            return rearProfileData[rowIndex]?.property;
          default:
            return null;
        }
      })();

      const finalErrorMessage = validateInput(temporaryValue, propertyName);
      const comparisonMessage = compareInput(temporaryValue, propertyName);

      if (!finalErrorMessage && !comparisonMessage) {
        const isNumericField = numericFields.includes(propertyName);
        let processedValue: string | number | null = temporaryValue
        if (isNumericField && !isNaN(Number(temporaryValue))) {
          processedValue = Number(temporaryValue);
        }
        const updateData = (data: any[]) => {
          return data.map((row, index) => {
            if (index === rowIndex) {
              return { ...row, [columnId]: processedValue };
            }
            return row;
          });
        };

        // Update the corresponding data based on the selected tab
        switch (selectedTab) {
          case "General Properties":
            setGeneralData(updateData(generalData));
            break;
          case "Front Profile":
            setFrontProfileData(updateData(frontProfileData));
            break;
          case "Side Profile":
            setSideProfileData(updateData(sideProfileData));
            break;
          case "Rear Profile":
            setRearProfileData(updateData(rearProfileData));
            break;
          default:
            break;
        }

        // Clear the error message if no issues
        setValidationErrors((prev) => ({
          ...prev,
          [`row-${rowIndex}`]: null,
        }));
      } else {
        // Revert to the previous valid value if validation fails
        setValidationErrors((prev) => ({
          ...prev,
          [`row-${rowIndex}`]: finalErrorMessage || comparisonMessage,
        }));

        // Allow resetting the value in the table to the previous one if invalid input
        switch (selectedTab) {
          case "General Properties":
            setTemporaryValue(generalData[rowIndex][columnId]);
            break;
          case "Front Profile":
            setTemporaryValue(frontProfileData[rowIndex][columnId]);
            break;
          case "Side Profile":
            setTemporaryValue(sideProfileData[rowIndex][columnId]);
            break;
          case "Rear Profile":
            setTemporaryValue(rearProfileData[rowIndex][columnId]);
            break;
          default:
            break;
        }
      }

      setEditableCell(null);
    }
  };

  const commonColumns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "property",
      header: "Property",
    },
    {
      accessorKey: "value",
      header: "Value",
      Cell: ({ cell, row, renderedCellValue }: any) => (
        editableCell?.rowIndex === row.index && editableCell?.columnId === "value" ? (
          <TextField
            value={temporaryValue}
            onChange={(e) => handleCellChange(e, row.original.property)}
            onBlur={handleCellBlur}
            error={!!validationErrors[`row-${row.index}`]}
            helperText={validationErrors[`row-${row.index}`] || ""}
            autoFocus
          />
        ) : (
          <div
            onClick={() => handleCellClick(row.index, "value")}
            style={{ cursor: "pointer", minWidth: "40px", padding: "4px" }}
          >
            {renderedCellValue ?? ''}
          </div>
        )
      ),
      size: 300,
    },
    {
      accessorKey: "units",
      header: "Units",
    },
  ];

  return (
    <div style={{ padding: "5px" }}>
      <Tabs value={selectedTab} onChange={handleTabChange}>
        <Tab label="General Properties" value="General Properties" />
        <Tab label="Front Profile" value="Front Profile" />
        <Tab label="Side Profile" value="Side Profile" />
        <Tab label="Rear Profile" value="Rear Profile" />
      </Tabs>
      <TabPanel value={selectedTab} name="General Properties">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={generalData}
          initialState={{ density: "compact" }}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
      <TabPanel value={selectedTab} name="Front Profile">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={frontProfileData}
          initialState={{ density: "compact" }}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
      <TabPanel value={selectedTab} name="Side Profile">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={sideProfileData}
          initialState={{ density: "compact" }}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
      <TabPanel value={selectedTab} name="Rear Profile">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={rearProfileData}
          initialState={{ density: "compact" }}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
    </div>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  name: string;
  value: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, name, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== name}
      id={`full-width-tabpanel-${name}`}
      aria-labelledby={`full-width-tab-${name}`}
      {...other}
    >
      {value === name && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

export default ParameterProperties;